import { calculateEventScore, determineIndexName, determineIndexNameWithFallback, isEventEnabled } from './howenScoreHelper';

/**
 * Parse any duration representation into seconds
 * @param {string|number} val
 * @returns {number} seconds
 */
export function parseDurationToSeconds(val) {
  if (val == null || val === '' || val === '-') return 0;
  if (typeof val === 'number') return val;
  const str = String(val).trim();
  if (!str) return 0;

  // Pure digits (seconds)
  if (/^\d+(\.\d+)?$/.test(str)) {
    return Math.round(parseFloat(str));
  }

  // HH:MM:SS or MM:SS
  if (str.includes(':')) {
    const parts = str.split(':').map((p) => parseInt(p, 10) || 0);
    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    }
    if (parts.length === 2) {
      return parts[0] * 60 + parts[1];
    }
  }

  return 0;
}

/**
 * Format total seconds into HH:mm:ss string
 * @param {number} totalSeconds
 * @returns {string} formatted duration e.g. "02:45:10"
 */
export function formatSecondsToDuration(totalSeconds) {
  const secs = Math.max(0, Math.round(Number(totalSeconds) || 0));
  const hours = Math.floor(secs / 3600);
  const minutes = Math.floor((secs % 3600) / 60);
  const seconds = secs % 60;

  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/**
 * Reusable event aggregation helper for a subset of daily rows
 * @param {Array} rowsSubset
 * @returns {{ recomputedEvents: Array, pctScore: number, totalWaypoints: number }}
 */
function aggregateEventsForRows(rowsSubset) {
  const eventMap = new Map();
  let totalWaypointsSubset = 0;

  rowsSubset.forEach((r) => {
    const waypoints = Number(r.total_waypoints ?? r.total_rows ?? 0);
    totalWaypointsSubset += waypoints;

    (r.events || []).forEach((ev) => {
      const key = ev.event_key;
      if (!key) return;
      if (!isEventEnabled(key, r.score_config_events)) return;

      let weight = 0;
      if (r.score_config_events && r.score_config_events[key] != null) {
        weight = Number(r.score_config_events[key]);
      } else if (ev.weight != null) {
        weight = Number(ev.weight);
      }
      if (weight <= 0) return;

      if (!eventMap.has(key)) {
        eventMap.set(key, {
          event_key: key,
          event_count: 0,
          total_rows: 0,
          weight,
          scores: [],
          evidence: []
        });
      }

      const curr = eventMap.get(key);
      curr.event_count += Number(ev.event_count || 0);
      curr.total_rows += Number(ev.total_rows ?? waypoints);
      if (Array.isArray(ev.evidence)) curr.evidence.push(...ev.evidence);
      if (ev.score != null) curr.scores.push(Number(ev.score));
    });
  });

  const recomputedEvents = [];
  eventMap.forEach((val, key) => {
    if (val.weight <= 0) return;
    const basisTotalRows = val.total_rows > 0 ? val.total_rows : totalWaypointsSubset;
    let score;
    if (val.scores.length > 0) {
      const avgScore = val.scores.reduce((sum, s) => sum + s, 0) / val.scores.length;
      score = parseFloat(avgScore.toFixed(2));
    } else {
      score = parseFloat(calculateEventScore(val.event_count, basisTotalRows, val.weight).toFixed(2));
    }
    recomputedEvents.push({
      event_key: key,
      event_count: val.event_count,
      total_rows: basisTotalRows,
      weight: val.weight,
      score,
      evidence: val.evidence
    });
  });

  const pctScore = recomputedEvents.reduce((sum, e) => sum + ((Number(e.score) || 0) / 10) * Number(e.weight || 0), 0);

  return { recomputedEvents, pctScore, totalWaypoints: totalWaypointsSubset };
}

/**
 * Aggregate daily driver score rows into 1 summarized row per Driver + Vehicle
 * @param {Array} dailyRows
 * @returns {Array} summarized rows
 */
export function summarizeDriverScoreRows(dailyRows) {
  if (!Array.isArray(dailyRows) || dailyRows.length === 0) return [];

  // Group by driver + vehicle
  const groupMap = new Map();

  dailyRows.forEach((row) => {
    const driverId = row.driver ?? row.driver_id ?? 'unknown_driver';
    const vehicleId = row.vehicle ?? row.vehicle_id ?? 'unknown_vehicle';
    const groupKey = `${driverId}_${vehicleId}`;

    if (!groupMap.has(groupKey)) {
      groupMap.set(groupKey, []);
    }
    groupMap.get(groupKey).push(row);
  });

  const summarizedList = [];

  groupMap.forEach((rows, groupKey) => {
    // Sort rows ascending by start_date for chronological breakdown
    rows.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

    const firstRow = rows[0];
    const lastRow = rows[rows.length - 1];

    // Earliest start_date and latest end_date
    const startDate = firstRow.start_date;
    const endDate = lastRow.end_date || firstRow.end_date;

    // Sum numerical fields
    let totalWaypoints = 0;
    let totalVssRows = 0;
    let hasVssRows = false;
    let totalDistanceKm = 0;
    let totalTripDurationSec = 0;
    let totalStopDurationSec = 0;

    // Violation events collection
    const combinedViolationEvents = [];

    rows.forEach((r) => {
      if (Array.isArray(r._violationEvents)) {
        combinedViolationEvents.push(...r._violationEvents);
      }

      if (r.vssTotalRows != null) {
        totalVssRows += Number(r.vssTotalRows) || 0;
        hasVssRows = true;
      }

      const waypoints = Number(r.total_waypoints ?? r.total_rows ?? 0);
      totalWaypoints += waypoints;
      totalDistanceKm += Number(r.total_distance_km ?? 0);

      const tripSec = parseDurationToSeconds(r.total_trip_duration ?? r.trip_duration);
      const stopSec = parseDurationToSeconds(r.total_stop_duration ?? r.stop_duration);
      totalTripDurationSec += tripSec;
      totalStopDurationSec += stopSec;
    });

    // Sort violation events ascending by event_time
    combinedViolationEvents.sort((a, b) => new Date(a.event_time) - new Date(b.event_time));

    // Aggregate subsets: Combine (all rows), Retase rows, and Non-Retase rows
    const retaseRows = rows.filter((r) => Array.isArray(r?.retase?.retase) && r.retase.retase.length > 0);
    const nonRetaseRows = rows.filter((r) => !(Array.isArray(r?.retase?.retase) && r.retase.retase.length > 0));

    const hasRetase = retaseRows.length > 0;
    const hasNonRetase = nonRetaseRows.length > 0;
    const hasBoth = hasRetase && hasNonRetase;

    // Retase subset metrics
    let totalWaypointsRetase = null;
    let totalDistanceKmRetase = null;
    let totalTripDurationSecRetase = null;
    let totalStopDurationSecRetase = null;
    let tripDurationStrRetase = null;
    let stopDurationStrRetase = null;

    if (hasRetase) {
      let waypointsSum = 0;
      let distanceSum = 0;
      let tripSecSum = 0;
      let stopSecSum = 0;

      retaseRows.forEach((r) => {
        waypointsSum += Number(r.total_waypoints ?? r.total_rows ?? 0);
        distanceSum += Number(r.total_distance_km ?? 0);
        tripSecSum += parseDurationToSeconds(r.total_trip_duration ?? r.trip_duration);
        stopSecSum += parseDurationToSeconds(r.total_stop_duration ?? r.stop_duration);
      });

      totalWaypointsRetase = waypointsSum;
      totalDistanceKmRetase = parseFloat(distanceSum.toFixed(2));
      totalTripDurationSecRetase = tripSecSum;
      totalStopDurationSecRetase = stopSecSum;
      tripDurationStrRetase = formatSecondsToDuration(tripSecSum);
      stopDurationStrRetase = formatSecondsToDuration(stopSecSum);
    }

    // Non-Retase subset metrics
    let totalWaypointsNonRetase = null;
    let totalDistanceKmNonRetase = null;
    let totalTripDurationSecNonRetase = null;
    let totalStopDurationSecNonRetase = null;
    let tripDurationStrNonRetase = null;
    let stopDurationStrNonRetase = null;

    if (hasNonRetase) {
      let waypointsSum = 0;
      let distanceSum = 0;
      let tripSecSum = 0;
      let stopSecSum = 0;

      nonRetaseRows.forEach((r) => {
        waypointsSum += Number(r.total_waypoints ?? r.total_rows ?? 0);
        distanceSum += Number(r.total_distance_km ?? 0);
        tripSecSum += parseDurationToSeconds(r.total_trip_duration ?? r.trip_duration);
        stopSecSum += parseDurationToSeconds(r.total_stop_duration ?? r.stop_duration);
      });

      totalWaypointsNonRetase = waypointsSum;
      totalDistanceKmNonRetase = parseFloat(distanceSum.toFixed(2));
      totalTripDurationSecNonRetase = tripSecSum;
      totalStopDurationSecNonRetase = stopSecSum;
      tripDurationStrNonRetase = formatSecondsToDuration(tripSecSum);
      stopDurationStrNonRetase = formatSecondsToDuration(stopSecSum);
    }

    const combineResult = hasBoth ? aggregateEventsForRows(rows) : { recomputedEvents: [], pctScore: 0, totalWaypoints: 0 };
    const retaseResult = hasRetase ? aggregateEventsForRows(retaseRows) : { recomputedEvents: [], pctScore: 0, totalWaypoints: 0 };
    const nonRetaseResult = hasNonRetase ? aggregateEventsForRows(nonRetaseRows) : { recomputedEvents: [], pctScore: 0, totalWaypoints: 0 };

    const scoreIndexes = firstRow.score_indexes || firstRow.idx_detail || firstRow.score_config?.score_indexes;

    // Combine: only exists if BOTH retase and non-retase days exist
    const recomputedEvents = hasBoth ? combineResult.recomputedEvents : [];
    const pctScore = hasBoth ? parseFloat(combineResult.pctScore.toFixed(2)) : null;
    const totalScore = pctScore;
    const idxName = hasBoth && pctScore != null ? determineIndexName(pctScore, scoreIndexes) : '-';

    // Retase: only exists if retase rows exist
    const pctScoreRetase = hasRetase ? parseFloat(retaseResult.pctScore.toFixed(2)) : null;
    const totalScoreRetase = pctScoreRetase;
    const idxRetaseResult =
      hasRetase && pctScoreRetase != null
        ? determineIndexNameWithFallback(pctScoreRetase, scoreIndexes)
        : { idx_name: '-', isEstimated: false, roundedDirection: null };

    // Non-Retase: only exists if non-retase rows exist
    const pctScoreNonRetase = hasNonRetase ? parseFloat(nonRetaseResult.pctScore.toFixed(2)) : null;
    const totalScoreNonRetase = pctScoreNonRetase;
    const idxNonRetaseResult =
      hasNonRetase && pctScoreNonRetase != null
        ? determineIndexNameWithFallback(pctScoreNonRetase, scoreIndexes)
        : { idx_name: '-', isEstimated: false, roundedDirection: null };

    // Format durations
    const tripDurationStr = formatSecondsToDuration(totalTripDurationSec);
    const stopDurationStr = formatSecondsToDuration(totalStopDurationSec);

    summarizedList.push({
      ...firstRow,
      id: `${firstRow.driver}-${firstRow.vehicle}-${groupKey}`,
      start_date: startDate,
      end_date: endDate,
      total_waypoints: totalWaypoints,
      vssTotalRows: hasVssRows ? totalVssRows : null,
      total_distance_km: parseFloat(totalDistanceKm.toFixed(2)),
      total_trip_duration: totalTripDurationSec,
      total_stop_duration: totalStopDurationSec,
      trip_duration: tripDurationStr,
      stop_duration: stopDurationStr,
      total_waypoints_retase: totalWaypointsRetase,
      total_distance_km_retase: totalDistanceKmRetase,
      total_trip_duration_retase: totalTripDurationSecRetase,
      total_stop_duration_retase: totalStopDurationSecRetase,
      trip_duration_retase: tripDurationStrRetase,
      stop_duration_retase: stopDurationStrRetase,
      total_waypoints_non_retase: totalWaypointsNonRetase,
      total_distance_km_non_retase: totalDistanceKmNonRetase,
      total_trip_duration_non_retase: totalTripDurationSecNonRetase,
      total_stop_duration_non_retase: totalStopDurationSecNonRetase,
      trip_duration_non_retase: tripDurationStrNonRetase,
      stop_duration_non_retase: stopDurationStrNonRetase,
      events: recomputedEvents,
      events_retase: hasRetase ? retaseResult.recomputedEvents : [],
      events_non_retase: hasNonRetase ? nonRetaseResult.recomputedEvents : [],
      total_score: totalScore,
      pct_score: pctScore,
      idx_name: idxName,
      total_score_retase: totalScoreRetase,
      pct_score_retase: pctScoreRetase,
      idx_name_retase: idxRetaseResult.idx_name,
      idx_name_retase_is_estimated: idxRetaseResult.isEstimated,
      idx_name_retase_rounded_direction: idxRetaseResult.roundedDirection,
      total_score_non_retase: totalScoreNonRetase,
      pct_score_non_retase: pctScoreNonRetase,
      idx_name_non_retase: idxNonRetaseResult.idx_name,
      idx_name_non_retase_is_estimated: idxNonRetaseResult.isEstimated,
      idx_name_non_retase_rounded_direction: idxNonRetaseResult.roundedDirection,
      _dailyBreakdown: rows,
      _violationEvents: combinedViolationEvents
    });
  });

  return summarizedList;
}
